import {useReducer, useEffect} from "react"
import {ItemWarehousePlanningDetails,} from "./itemwarehouseplanningdetails"
import {Warehouse} from "./api/warehouse"
import {ItemWarehouse} from "./api/itemwarehouse"
import {PlannedSalesReturn,  PlannedSalesReturnLine, PSRStatus
} from "./api/plannedsalesreturn"
import {WarehouseTransfer, WarehouseTransferLine, WTStatus
} from "./api/warehousetransfer"
import {Item} from "./api/item"
import {SalesOrder,  SalesOrderLine, SOStatus, } from "./api/salesorder"
import {PurchaseOrder,  PurchaseOrderLine, POStatus, } from "./api/purchaseorder"
import {Filters} from "./api/types"
import {ISODate} from "./date"
import {ProjectedStock, calculateProjectedStocks} from "./projectedstock"
import {ADError, ADWarning, ALL_CHECKS} from "./alldatachecks"
import {useDivision} from "./shared"
import utils from "./utils"
import {tuple, compare as tupleCompare} from "./tuple"
import {BLACKLIST_IDS} from "./blacklist"
import {api} from "./shared"

const TODAY = ISODate.localToday()

export const apiItemsLoading = {
  items: Item,
  warehouses: Warehouse,
  purchaseOrders: PurchaseOrder,
  salesOrders: SalesOrder,
  plannedSalesReturns: PlannedSalesReturn,
  warehouseTransfers: WarehouseTransfer,
  itemWarehouses: ItemWarehouse,
} as const

export type ApiItemsLoading = typeof apiItemsLoading

export type ApiItemLoadingClasses = ApiItemsLoading[keyof ApiItemsLoading]
export type ApiItemLoading = InstanceType<ApiItemLoadingClasses>

type KeyForApiItemsLoadingClass<CLASS extends ApiItemLoadingClasses> =
  keyof {[Key in keyof ApiItemsLoading as ApiItemsLoading[Key] extends CLASS ? Key : never]: any}

export class ItemsLoading<CLASS extends ApiItemLoadingClasses> {
  constructor(
    public readonly loadedItems: ReadonlyArray<InstanceType<CLASS>>,
    public readonly done: boolean,
    public readonly loadError: string | null
  ){}

  addItems(items: ReadonlyArray<InstanceType<CLASS>>): ItemsLoading<CLASS> {
    if (this.done || this.loadError) {
      throw new Error("Impossible to add items")
    }
    return new ItemsLoading([...this.loadedItems, ...items], false, null)
  }

  setDone(): ItemsLoading<CLASS> {
    if (this.done || this.loadError) {
      throw new Error("Impossible to set to done")
    }
    return new ItemsLoading(this.loadedItems, true, null)
  }

  setError(error: string): ItemsLoading<CLASS> {
    if (this.done || this.loadError) {
      throw new Error("Impossible to set error")
    }
    return new ItemsLoading(this.loadedItems, false, error)
  }
}

export const apiItems = {
  ...apiItemsLoading,
  purchaseOrderLines: PurchaseOrderLine,
  salesOrderLines: SalesOrderLine,
  plannedSalesReturnLines: PlannedSalesReturnLine,
  warehouseTransferLines: WarehouseTransferLine,
  itemWarehousePlanningDetails_s: ItemWarehousePlanningDetails,
} as const

export type ApiItems = typeof apiItems

export type ApiItem = InstanceType<ApiItems[keyof ApiItems]>

type AllDataDoneInterface = {
  [K in keyof ApiItems]: ReadonlyArray<InstanceType<ApiItems[K]>>
}

// using this object as placeholder in the context
export const dummyAllDataDoneObject: AllDataDone = {
  done: true
} as AllDataDone
const throwError = function() {
  throw new Error("Using the dummy data object; this should not be possible")
}
Object.keys(apiItems).forEach(key => {
  Object.defineProperty(dummyAllDataDoneObject, key, {get: throwError})
})
// end: using this object as placeholder in the context

export class AllDataDone implements AllDataDoneInterface {
  readonly done: true
  readonly projectedStocks: readonly ProjectedStock[]
  readonly errors: readonly ADError[]
  readonly warnings: readonly ADWarning[]
  readonly items: readonly Item[]
  readonly warehouses: readonly Warehouse[]
  readonly purchaseOrders: readonly PurchaseOrder[]
  readonly purchaseOrderLines: readonly PurchaseOrderLine[]
  readonly salesOrders: readonly SalesOrder[]
  readonly salesOrderLines: readonly SalesOrderLine[]
  readonly plannedSalesReturns: readonly PlannedSalesReturn[]
  readonly plannedSalesReturnLines: readonly PlannedSalesReturnLine[]
  readonly warehouseTransfers: readonly WarehouseTransfer[]
  readonly warehouseTransferLines: readonly WarehouseTransferLine[]
  readonly itemWarehousePlanningDetails_s: readonly ItemWarehousePlanningDetails[]
  readonly itemWarehouses: readonly ItemWarehouse[]

  constructor (
    public readonly firstDataRetrieved: Date,
    public readonly division: number,
    public readonly _rawItems: readonly Item[],
    public readonly _rawWarehouses: readonly Warehouse[],
    public readonly _rawPurchaseOrders: readonly PurchaseOrder[],
    public readonly _rawSalesOrders: readonly SalesOrder[],
    public readonly _rawPlannedSalesReturns: readonly PlannedSalesReturn[],
    public readonly _rawWarehouseTransfers: readonly WarehouseTransfer[],
    public readonly _rawItemWarehouses: readonly ItemWarehouse[],
    public readonly tick: () => void,
  ) {
    this.done = true
    this.items = itemPostLoadFilter(_rawItems)
    this.warehouses = warehousePostLoadFilter(_rawWarehouses)
    this.itemWarehouses = itemWarehousesPostLoadFilter(_rawItemWarehouses, this.items)
    const itemCodes = new Set(this.items.map(item => item.code))
    const warehouseCodes = new Set(this.warehouses.map(warehouse => warehouse.code))
    this.purchaseOrderLines = this._rawPurchaseOrders
      .filter(po => !BLACKLIST_IDS.has(po.purchaseOrderID))
      .filter(po => warehouseCodes.has(po.warehouseCode))
      .flatMap(po => po.purchaseOrderLines)
      .filter(pol => itemCodes.has(pol.itemCode))
      .filter(pol => pol.receivedQuantity < pol.quantity)
      .filter(pol => pol.$calc.dropShipment === null)
      .filter(pol => pol.$calc.poRedirect === null)

    this.purchaseOrders =
      [...new Set(this.purchaseOrderLines.map(pol => pol.parent))]

    this.salesOrderLines = this._rawSalesOrders
      .filter(so => !BLACKLIST_IDS.has(so.orderID))
      .filter(so => warehouseCodes.has(so.warehouseCode))
      .flatMap(so => so.salesOrderLines)
      .filter(sol => itemCodes.has(sol.itemCode))
      .filter(sol => sol.deliveryStatus === SOStatus.OPEN
              || sol.deliveryStatus === SOStatus.PARTIAL)
      .filter(sol => Math.abs(sol.quantityDelivered) < Math.abs(sol.quantity))
      .filter(sol => sol.useDropShipment === 0)
      .filter(sol => sol.$calc.dropShipment === null)

    this.salesOrders = [...new Set(this.salesOrderLines.map(sol => sol.parent))]

    this.plannedSalesReturnLines = this._rawPlannedSalesReturns
      .filter(psr => !BLACKLIST_IDS.has(psr.plannedSalesReturnID))
      .filter(psr => warehouseCodes.has(psr.warehouseCode))
      .flatMap(psr => psr.plannedSalesReturnLines)
      .filter(psrl => itemCodes.has(psrl.itemCode))
      .filter(psrl => (psrl.receivedQuantity ?? 0) < psrl.plannedReturnQuantity)

    this.plannedSalesReturns =
      [...new Set(this.plannedSalesReturnLines.map(sol => sol.parent))]

    this.warehouseTransferLines = this._rawWarehouseTransfers
      .filter(wt => !BLACKLIST_IDS.has(wt.transferID))
      .filter(wt => warehouseCodes.has(wt.warehouseFromCode)
              || warehouseCodes.has(wt.warehouseToCode))
      .flatMap(wt => wt.warehouseTransferLines)
      .filter(wtl => itemCodes.has(wtl.itemCode))
      .filter(wtl => wtl.$calc.poRedirect === null)

    this.warehouseTransfers =
      [...new Set(this.warehouseTransferLines.map(wtl => wtl.parent))]

    this.itemWarehousePlanningDetails_s =
      getItemWarehousePlanningDetailsFromTransfers(
        this.purchaseOrderLines,
        this.salesOrderLines,
        this.plannedSalesReturnLines,
        this.warehouseTransferLines,
        this.warehouses
    )

    this.projectedStocks = (() => {
      const itemIndexByCode = new Map(this.items.map(
        (item, index) => [item.code, index]))
      const warehouseIndexByCode = new Map(this.warehouses.map(
        (warehouse, index) => [warehouse.code, index]))
      return [...calculateProjectedStocks(
        this.itemWarehouses,
        this.itemWarehousePlanningDetails_s,
      )].sort((a, b) => {
        const warehouseDiff = warehouseIndexByCode.get(a.warehouseCode)! -
          warehouseIndexByCode.get(b.warehouseCode)!
        const itemDiff = itemIndexByCode.get(a.itemCode)! -
          itemIndexByCode.get(b.itemCode)!
        return warehouseDiff || itemDiff
      })
    })()

    const errorsOrWarnings = ALL_CHECKS.map(check => check(this))
      .filter(errorsOrWarning => errorsOrWarning.count > 0)
    this.errors = errorsOrWarnings.filter(
      (issue): issue is ADError => issue.level === "error")
    this.warnings = errorsOrWarnings.filter(
      (issue): issue is ADWarning => issue.level === "warning")
  }

  clone(): AllDataDone {
    return new AllDataDone(
      this.firstDataRetrieved,
      this.division,
      this.items,
      this.warehouses,
      this._rawPurchaseOrders,
      this._rawSalesOrders,
      this._rawPlannedSalesReturns,
      this._rawWarehouseTransfers,
      this.itemWarehouses,
      this.tick,
    )
  }

}

type AllDataLoadingInterface = {
  [K in keyof ApiItemsLoading]: ItemsLoading<ApiItemsLoading[K]>
}

export class AllDataLoading implements AllDataLoadingInterface{
  static allDataSerialNumber = 0
  readonly done: false
  constructor (
    public readonly firstDataRetrieved: Date | null,
    public readonly division: number,
    public readonly items: ItemsLoading<typeof Item>,
    public readonly warehouses: ItemsLoading<typeof Warehouse>,
    public readonly purchaseOrders: ItemsLoading<typeof PurchaseOrder>,
    public readonly salesOrders: ItemsLoading<typeof SalesOrder>,
    public readonly plannedSalesReturns: ItemsLoading<typeof PlannedSalesReturn>,
    public readonly warehouseTransfers: ItemsLoading<typeof WarehouseTransfer>,
    public readonly itemWarehouses: ItemsLoading<typeof ItemWarehouse>,
  ) {
    this.done = false
  }

  static empty(division: number): AllDataLoading {
    return new AllDataLoading(
      null,
      division,
      new ItemsLoading(new Array<Item>(), false, null) ,
      new ItemsLoading(new Array<Warehouse>(), false, null) ,
      new ItemsLoading(new Array<PurchaseOrder>(), false, null) ,
      new ItemsLoading(new Array<SalesOrder>(), false, null) ,
      new ItemsLoading(new Array<PlannedSalesReturn>(), false, null) ,
      new ItemsLoading(new Array<WarehouseTransfer>(), false, null) ,
      new ItemsLoading(new Array<ItemWarehouse>(), false, null) ,
    )
  }

  updateSetFirstDataRetrievedIfUnset(
    update: Partial<{[K in keyof ApiItemsLoading]: AllDataLoading[K]}>
  ): AllDataLoading {
    return new AllDataLoading(
      this.firstDataRetrieved ?? new Date(),
      this.division,
      update.items ?? this.items,
      update.warehouses ?? this.warehouses,
      update.purchaseOrders ?? this.purchaseOrders,
      update.salesOrders ?? this.salesOrders,
      update.plannedSalesReturns ?? this.plannedSalesReturns,
      update.warehouseTransfers ?? this.warehouseTransfers,
      update.itemWarehouses ?? this.itemWarehouses,
    )
  }

  addData<CLASS extends ApiItemLoadingClasses>(
    division: number, key: KeyForApiItemsLoadingClass<CLASS>,
    data: ReadonlyArray<InstanceType<CLASS>>,
  ): AllDataLoading {
    if (division !== this.division) {
      throw new Error("Division does not match")
    }
    const allDataLoading = this.updateSetFirstDataRetrievedIfUnset({
      [key]: (this[key] as ItemsLoading<CLASS>).addItems(data)})
    return allDataLoading
  }

  setItemLoadingDone<CLASS extends ApiItemLoadingClasses>(
    division: number, key: KeyForApiItemsLoadingClass<CLASS>,
  ): AllDataLoading {
    if (division !== this.division) {
      throw new Error("Division does not match")
    }
    const allDataLoading = this.updateSetFirstDataRetrievedIfUnset(
      {[key]: this[key].setDone()})
    return allDataLoading
  }

  setItemLoadingError<CLASS extends ApiItemLoadingClasses>(
    division: number, key: KeyForApiItemsLoadingClass<CLASS>, error: string,
  ): AllDataLoading {
    if (division !== this.division) {
      throw new Error("Division does not match")
    }
    const allDataLoading = this.updateSetFirstDataRetrievedIfUnset(
      {[key]: this[key].setError(error)})
    return allDataLoading
  }

  createDoneIfPossible(tick: () => void): AllData {
    const keys = Object.keys(apiItemsLoading) as (keyof typeof apiItemsLoading)[]
    if (keys.every(k => this[k].done)) {
      return new AllDataDone(
        this.firstDataRetrieved!,
        this.division,
        this.items.loadedItems,
        this.warehouses.loadedItems,
        this.purchaseOrders.loadedItems,
        this.salesOrders.loadedItems,
        this.plannedSalesReturns.loadedItems,
        this.warehouseTransfers.loadedItems,
        this.itemWarehouses.loadedItems,
        tick,
      )
    } else {
      return this
    }
  }
}

export type AllData = AllDataLoading | AllDataDone


const itemFilter: Filters<typeof Item> = {
  isStockItem: {"eq": true},
  isSalesItem: {"eq": true},
  isPurchaseItem: {"eq": true},
}

const poFilter: Filters<typeof PurchaseOrder> = {
  receiptStatus: {"in": [POStatus.OPEN, POStatus.PARTIAL]},
  dropShipment: {"eq": false},
}

const soFilter: Filters<typeof SalesOrder> = {
  status: {"in": [SOStatus.OPEN, SOStatus.PARTIAL]},
}

const psrFilter: Filters<typeof PlannedSalesReturn> = {
  status: {"in": [PSRStatus.OPEN]},
}

const wtFilter: Filters<typeof WarehouseTransfer> = {
  status: {"eq": WTStatus.OPEN}
}

function initialAllData([division, serial]: [number, number]): CreatedState {
  return {
    status: "created",
    serial,
    allData: AllDataLoading.empty(division)
  }
}

type CreatedState = {status: "created", serial: number, allData: AllDataLoading}
type LoadingState = {status: "loading", serial: number, allData: AllDataLoading}
type ErrorState = {status: "error", serial: number, allData: AllDataLoading}
type DoneState = {status: "done", serial: number, allData: AllDataDone}

type State = never
  | CreatedState
  | ErrorState
  | LoadingState
  | DoneState

type AddDataAction<CLASS extends ApiItemLoadingClasses> = {
  type: "addData", serial: number, division: number,
  key: KeyForApiItemsLoadingClass<CLASS>, data: ReadonlyArray<InstanceType<CLASS>>,
}
type ItemLoadingDoneAction<CLASS extends ApiItemLoadingClasses> = {
  type: "itemLoadingDone", serial: number, division: number,
  key: KeyForApiItemsLoadingClass<CLASS>
}
type ItemLoadingErrorAction<CLASS extends ApiItemLoadingClasses> = {
  type: "itemLoadingError", serial: number, division: number,
  key: KeyForApiItemsLoadingClass<CLASS>, error: string,
}
type ResetAction = {type: "reset", division: number}
type StartLoadAction = {type: "startLoad", serial: number}
type DoneLoadingAction = {type: "doneLoad", serial: number, tick: () => void}
type TickAction = {type: "tick", serial: number}

type Action = never  // just here so that the rest lines out nicely :)
  | ResetAction
  | StartLoadAction
  | AddDataAction<ApiItemLoadingClasses>
  | ItemLoadingDoneAction<ApiItemLoadingClasses>
  | ItemLoadingErrorAction<ApiItemLoadingClasses>
  | DoneLoadingAction
  | TickAction


class WrongSerialError extends Error {
  constructor(expectedSerial: number, givenSerial: number) {
    super(`Wrong serial; expected ${expectedSerial} but got ${givenSerial}`)
  }
}


/*

// something makes this not work....
function reducer(state: State, action: ResetAction): CreatedState;
function reducer(state: CreatedState, action: StartLoadAction): LoadingState;
function reducer(state: LoadingState, action:
  AddDataAction<ApiItemLoadingClasses>|ItemLoadingDoneAction<ApiItemLoadingClasses>
): LoadingState;
function reducer(
  state: ErrorState, action: AddDataAction<ApiItemLoadingClasses>): ErrorState;
function reducer(
  state: ErrorState, action: AddDataAction<ApiItemLoadingClasses>): ErrorState;
function reducer(state: LoadingState | ErrorState,
  action: ItemLoadingErrorAction<ApiItemLoadingClasses>): ErrorState;
function reducer(state: ErrorState, action: DoneLoadingAction): ErrorState;
function reducer(state: LoadingState, action: DoneLoadingAction): DoneState;
function reducer(state: DoneState, action: TickAction): DoneState;
*/
function reducer(state: State, action: Action): State {
  console.log("reducer", {state, action});
  if (action.type !== "reset") {
    if (action.serial !== state.serial) {
      throw new WrongSerialError(state.serial, action.serial)
    }
  }
  if (!(
    (action.type === "reset")
    || (state.status === "created" && action.type === "startLoad")
    || ((state.status === "loading" || state.status === "error")
      && (action.type === "addData" || action.type === "itemLoadingDone"
        || action.type === "itemLoadingError"))
    || ((state.status === "loading" || state.status === "error")
      && action.type === "doneLoad")
    || (state.status === "done" && action.type === "tick"))
  ) {
    throw new Error(`Action ${action.type} invalid in state ${state.status}`)
  }
  switch (action.type) {
    case "reset":
      return initialAllData([action.division, state.serial + 1])
    case "startLoad":
      if (state.status !== "created") {
        throw new Error("Wrong status")
      }
      return {status: "loading", serial: state.serial, allData: state.allData}
    case "addData":
      if (state.status !== "loading" && state.status !== "error") {
        throw new Error("Wrong status")
      }
      return {...state, allData: state.allData.addData(
        action.division, action.key, action.data)}
    case "itemLoadingDone":
      if (state.status !== "loading" && state.status !== "error") {
        throw new Error("Wrong status")
      }
      return {...state, allData: state.allData.setItemLoadingDone(
        action.division, action.key)}
    case "itemLoadingError":
      if (state.status !== "loading" && state.status !== "error") {
        throw new Error("Wrong status")
      }
      return {...state, status: "error", allData: state.allData.setItemLoadingError(
        action.division, action.key, action.error)}
    case "doneLoad":
      if (state.status === "loading") {
        const newAllData = state.allData.createDoneIfPossible(action.tick)
        if (!newAllData.done) {
          throw new Error("Should not call doneLoad when not done")
        }
        return {status: "done", serial: state.serial, allData: newAllData}
      } else if (state.status === "error") {
        // calling "done" on an error state is a no-op
        return state
      } else {
        throw new Error("Wrong status")
      }
    case "tick":
      if (state.status !== "done" || !state.allData.done) {
        throw new Error("Cannot tick on not done allData")
      }
      return {status: "done", serial: state.serial, allData: state.allData.clone()}
  }
}

function itemPostLoadFilter(items: ReadonlyArray<Item>): ReadonlyArray<Item> {
  return items
  .filter(item => item.startDate.le(TODAY) && (
    item.endDate === null || item.endDate.ge(TODAY)))
  .sort((a, b) => a.code.localeCompare(b.code))
}

function itemWarehousesPostLoadFilter(
  itemWarehouses: ReadonlyArray<ItemWarehouse>,
  items: ReadonlyArray<Item>
): ReadonlyArray<ItemWarehouse> {
  const validItemCodes = new Set(items.map(item => item.code))
  return itemWarehouses.filter(iw => validItemCodes.has(iw.itemCode))
}

function warehousePostLoadFilter(
  warehouses: ReadonlyArray<Warehouse>
): ReadonlyArray<Warehouse> {
    const mainWarehouse = warehouses.find(wh => wh.main === 1)
    let warehouseFiter = (_: Warehouse) => true
    if (mainWarehouse) {
      const suffix = mainWarehouse.description.split(/\b/).slice(-1)[0]
      const isSymbolSuffix = !!suffix.match(/[^\w\s]+/)
      if (isSymbolSuffix) {
        console.log("Using warehouse filter suffix", {suffix})
        warehouseFiter = (wh: Warehouse) => {
          const whSuffix = wh.description.split(/\b/).slice(-1)[0]
          return whSuffix === suffix
        }
      }
    }

    return warehouses
    .filter(warehouseFiter)
    .sort((a, b) => (a.main - b.main) || a.code.localeCompare(b.code))
}

async function loadAllData(
  division: number, serial: number, dispatch: (action: Action) => void
): Promise<void> {
  type FilterDef = Partial<{
    [key in keyof ApiItemsLoading]: Filters<ApiItemsLoading[key]>
  }>

  const filterDefs: FilterDef  = {
    items: itemFilter,
    warehouses: {},
    purchaseOrders: poFilter,
    salesOrders: soFilter,
    plannedSalesReturns: psrFilter,
    warehouseTransfers: wtFilter,
  } as const

  function getLoadPromise<CLASS extends ApiItemLoadingClasses>(
    [key, filter]: [KeyForApiItemsLoadingClass<CLASS>, Filters<CLASS>],
  ): [
    KeyForApiItemsLoadingClass<CLASS>, Promise<ReadonlyArray<InstanceType<CLASS>>>
  ] {
    return [key, loadData(division, serial, key, filter, dispatch)]
  }

  try {
    const promises = Object.fromEntries(Object.entries(filterDefs).map(
      ([key, value]) => getLoadPromise([key as keyof typeof filterDefs, value])))

    const warehouses = await promises.warehouses as ReadonlyArray<Warehouse>

    const itemWarehousesFilter: Filters<typeof ItemWarehouse> = {
      warehouseCode: {"in": warehousePostLoadFilter(warehouses).map(w => w.code)}
    }
    promises.itemWarehouses = loadData(
      division, serial, "itemWarehouses", itemWarehousesFilter, dispatch)
    for (const promise of Object.values(promises)) {
      await promise
    }
    dispatch(
      {type: "doneLoad", serial, tick: () => {dispatch({type: "tick", serial})}})
  } catch (e) {
    if (e instanceof WrongSerialError) {
      console.log("Wrong serial")
      return
    }
    throw e
  }
}

async function loadData<CLASS extends ApiItemLoadingClasses>(
  division: number,
  serial: number,
  key: KeyForApiItemsLoadingClass<CLASS>,
  filter: Filters<CLASS>,
  dispatch: (action: Action) => void,
): Promise<ReadonlyArray<InstanceType<CLASS>>> {
  let data = new Array<InstanceType<CLASS>>()
  try {
    for await (const newData
      of (apiItemsLoading[key] as CLASS).getAll(api, division, filter)
    ) {
      dispatch({type: "addData", division, serial, key, data: newData})
      data = [...data, ...newData]
    }
  } catch (e) {
    if (e instanceof WrongSerialError) {
      throw e
    }
    dispatch({type: "itemLoadingError", division, serial, key, error: (e as Error).message})
    throw e
  }
  dispatch({type: "itemLoadingDone", division, serial, key})
  return data
}

export function useAllData(): AllData {
  const division = useDivision()
  const [state, dispatch] = useReducer(
    reducer, [division, 0] as [number, number], initialAllData)

  useEffect(() => {
    if (state.allData.division !== division) {
      dispatch({type: "reset", division})
    }
  }, [division, state.allData.division])

  useEffect(() => {
    if (state.status !== "created") {
      return
    }
    try {
      dispatch({type: "startLoad", serial: state.serial})
    } catch (e) {
      if (e instanceof WrongSerialError) {
        throw new Error("Wrong serial during startLoad")
      }
      return
    }
    loadAllData(state.allData.division, state.serial, dispatch)
  }, [state])

  return state.allData
}

function getItemWarehousePlanningDetailsFromTransfers(
  purchaseOrderLines: readonly PurchaseOrderLine[],
  salesOrderLines: readonly SalesOrderLine[],
  plannedSalesReturnLines: readonly PlannedSalesReturnLine[],
  warehouseTransferLines: readonly WarehouseTransferLine[],
  warehouses: readonly Warehouse[],
): readonly ItemWarehousePlanningDetails[] {
  const iwpds: readonly ItemWarehousePlanningDetails[] = [
    ...purchaseOrderLines.map(pol => new ItemWarehousePlanningDetails(
      pol.itemCode,
      pol.receiptDate,
      pol.quantity - pol.receivedQuantity,
      pol.parent.warehouseCode,
      pol,
    )),
    ...salesOrderLines.map(sol => new ItemWarehousePlanningDetails(
      sol.itemCode,
      sol.deliveryDate,
      -(sol.quantity - sol.quantityDelivered),
      sol.parent.warehouseCode,
      sol,
    )),
    ...plannedSalesReturnLines.map(psrl => new ItemWarehousePlanningDetails(
      psrl.itemCode,
      psrl.parent.returnDate,
      psrl.plannedReturnQuantity - (psrl.receivedQuantity ?? 0),
      psrl.parent.warehouseCode,
      psrl,
    )),
    ...warehouseTransferLines.flatMap(
      wtl => [
        new ItemWarehousePlanningDetails(
          // out warehouse
          wtl.itemCode,
          wtl.parent.plannedDeliveryDate,
          -wtl.quantity,
          wtl.parent.warehouseFromCode,
          wtl,
        ),
        new ItemWarehousePlanningDetails(
          // in warehouse
          wtl.itemCode,
          wtl.parent.plannedReceiptDate,
          wtl.quantity,
          wtl.parent.warehouseToCode,
          wtl
        )
      ]),
  ]
  const warehouseCodes = new Set(warehouses.map(wh => wh.code))

  return iwpds
    .filter(iwpd => warehouseCodes.has(iwpd.warehouseCode))
    .sort(utils.By(iwpd => tuple(
      [iwpd.plannedDate, iwpd.getPlanningSourceType(),
        iwpd.getPlanningSourceNumber(), iwpd.planningSource.lineNumber]),
      tupleCompare))
}
